import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { localDateStr } from '../utils/dateUtils';

// Leaflet imports — CSS must load before map renders
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix default marker icons broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const OUTCOMES = ['positive','neutral','negative'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const outcomeColors = { positive: 'success', neutral: 'neutral', negative: 'error' };
const outcomeDotColors = { positive: '#10b981', neutral: '#f59e0b', negative: '#ef4444' };

const defaultForm = {
  party_type: 'customer', customer_id: '', client_id: '',
  unregistered_name: '', unregistered_info: '',
  visit_date: '', visit_time: '', purpose: '', products_discussed: '',
  notes: '', outcome: 'neutral', samples_given: '', next_visit_date: '',
  dcr_purpose: '', dcr_products_discussed: '', dcr_samples_given: '',
  dcr_next_action: '', dcr_doctor_feedback: '', dcr_submitted: false,
  check_in_lat: null, check_in_lng: null, check_in_time: null, check_in_address: null,
};

// ── Haversine for display (km) ────────────────────────────────────────────────
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Map tab component (vanilla Leaflet inside a div ref) ──────────────────────
function VisitMap({ visits }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
    const map = L.map(mapRef.current).setView([20.5937, 78.9629], 5);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    const validVisits = visits.filter(v => v.check_in_lat != null && v.check_in_lng != null);
    validVisits.forEach(v => {
      const color = v.geo_flagged ? '#ef4444' : (outcomeDotColors[v.outcome] || '#6b7280');
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      const checkin = new Date(v.check_in_time).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
      const popup = `
        <div style="font-size:13px;min-width:160px">
          <div style="font-weight:700;margin-bottom:4px">${(v.visitor_type === 'unregistered' ? v.unregistered_name : (v.client_id ? v.client_name : v.customer_name)) || '—'}</div>
          <div style="color:#555">MR: ${v.user_name || '—'}</div>
          <div style="color:#555">Date: ${v.visit_date ? new Date(v.visit_date).toLocaleDateString('en-IN') : '—'}</div>
          <div style="color:#555">Check-in: ${checkin}</div>
          ${v.geo_flagged ? '<div style="color:#ef4444;font-weight:600;margin-top:4px">⚠ Outside geofence</div>' : ''}
        </div>`;
      L.marker([parseFloat(v.check_in_lat), parseFloat(v.check_in_lng)], { icon })
        .addTo(map)
        .bindPopup(popup);
    });

    if (validVisits.length > 0) {
      const bounds = L.latLngBounds(validVisits.map(v => [parseFloat(v.check_in_lat), parseFloat(v.check_in_lng)]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }

    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [visits]);

  const geoCount = visits.filter(v => v.check_in_lat != null).length;
  const flaggedCount = visits.filter(v => v.geo_flagged).length;

  return (
    <div className="card">
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          <strong>{geoCount}</strong> check-ins plotted
        </span>
        {flaggedCount > 0 && (
          <span style={{ fontSize: '0.82rem', color: '#ef4444', fontWeight: 600 }}>
            ⚠ {flaggedCount} outside geofence
          </span>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Dots: <span style={{ color:'#10b981' }}>●</span> positive &nbsp;
          <span style={{ color:'#f59e0b' }}>●</span> neutral &nbsp;
          <span style={{ color:'#6b7280' }}>●</span> negative &nbsp;
          <span style={{ color:'#ef4444' }}>⬤</span> geo-flagged
        </span>
      </div>
      {geoCount === 0 ? (
        <div className="empty-state" style={{ padding: 40 }}>
          <p>No geo check-ins yet. Use the Check-In button when recording a visit.</p>
        </div>
      ) : (
        <div ref={mapRef} style={{ height: 480, width: '100%' }} />
      )}
    </div>
  );
}

// ── GPS capture hook ──────────────────────────────────────────────────────────
function useGpsCapture() {
  const [gpsState, setGpsState] = useState('idle'); // idle | loading | done | error
  const [gpsError, setGpsError] = useState('');

  const capture = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser/device.'));
        return;
      }
      setGpsState('loading');
      setGpsError('');
      navigator.geolocation.getCurrentPosition(
        pos => {
          setGpsState('done');
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        },
        err => {
          setGpsState('error');
          const msg = err.code === 1 ? 'Location permission denied. Enable it in browser settings.' :
            err.code === 2 ? 'Location unavailable. Try again.' : 'Location request timed out.';
          setGpsError(msg);
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }, []);

  return { gpsState, gpsError, capture, setGpsState };
}

export default function Visits() {
  const { user, isPrivileged } = useAuth();
  const now = new Date();

  const [tab, setTab] = useState('visits'); // 'visits' | 'dcr' | 'map'
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [viewModal, setViewModal] = useState({ open: false, visit: null });
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [dcrOpen, setDcrOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // visitId being checked out

  // Calendar view state
  const [calView, setCalView] = useState(false);
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calDayPopup, setCalDayPopup] = useState(null);

  // DCR Report state
  const [dcrRows, setDcrRows] = useState([]);
  const [dcrLoading, setDcrLoading] = useState(false);
  const [dcrYear, setDcrYear] = useState(now.getFullYear());
  const [dcrMonth, setDcrMonth] = useState(now.getMonth() + 1);
  const [dcrUserId, setDcrUserId] = useState('');
  const [dcrUsers, setDcrUsers] = useState([]);

  const { gpsState, gpsError, capture, setGpsState } = useGpsCapture();

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/visits', { params: { page, limit: 15, search: debouncedSearch||undefined, outcome: outcomeFilter||undefined } });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, outcomeFilter]);

  useEffect(() => { load(); }, [load]);

  const loadDCR = useCallback(async () => {
    setDcrLoading(true);
    try {
      const params = { year: dcrYear, month: dcrMonth };
      if (dcrUserId) params.user_id = dcrUserId;
      const { data: res } = await api.get('/visits/dcr-report', { params });
      setDcrRows(res.data?.data || res.data || []);
    } catch { setDcrRows([]); }
    finally { setDcrLoading(false); }
  }, [dcrYear, dcrMonth, dcrUserId]);

  useEffect(() => {
    if (tab === 'dcr') {
      loadDCR();
      if (isPrivileged) {
        api.get('/users', { params: { limit: 200, role: 'mr' } }).then(r => {
          const users = r.data?.data || r.data || [];
          setDcrUsers(Array.isArray(users) ? users : []);
        }).catch(() => setDcrUsers([]));
      }
    }
  }, [tab, loadDCR, isPrivileged]);

  const openAdd = async () => {
    const [custRes, cliRes] = await Promise.all([
      api.get('/customers', { params: { limit: 200 } }),
      api.get('/clients', { params: { limit: 200, approval_status: 'approved' } }),
    ]);
    setCustomers(custRes.data.data || []);
    setClients(cliRes.data.data || []);
    setForm({ ...defaultForm, visit_date: localDateStr() });
    setGpsState('idle');
    setError(''); setDcrOpen(false); setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = async (v) => {
    const [custRes, cliRes] = await Promise.all([
      api.get('/customers', { params: { limit: 200 } }),
      api.get('/clients', { params: { limit: 200, approval_status: 'approved' } }),
    ]);
    setCustomers(custRes.data.data || []);
    setClients(cliRes.data.data || []);
    const partyType = v.visitor_type === 'unregistered' ? 'unregistered' : (v.client_id ? 'client' : 'customer');
    setForm({
      party_type: partyType,
      customer_id: v.customer_id || '', client_id: v.client_id || '',
      unregistered_name: v.unregistered_name || '', unregistered_info: v.unregistered_info || '',
      visit_date: v.visit_date?.split('T')[0] || '', visit_time: v.visit_time || '',
      purpose: v.purpose || '', products_discussed: v.products_discussed || '',
      notes: v.notes || '', outcome: v.outcome || 'neutral',
      samples_given: v.samples_given || '', next_visit_date: v.next_visit_date?.split('T')[0] || '',
      dcr_purpose: v.dcr_purpose || '', dcr_products_discussed: v.dcr_products_discussed || '',
      dcr_samples_given: v.dcr_samples_given || '', dcr_next_action: v.dcr_next_action || '',
      dcr_doctor_feedback: v.dcr_doctor_feedback || '', dcr_submitted: !!v.dcr_submitted,
      check_in_lat: v.check_in_lat || null, check_in_lng: v.check_in_lng || null,
      check_in_time: v.check_in_time || null, check_in_address: v.check_in_address || null,
    });
    setGpsState(v.check_in_lat ? 'done' : 'idle');
    setError(''); setDcrOpen(false); setModal({ open: true, mode: 'edit', item: v });
  };

  const handleCheckIn = async () => {
    try {
      const { lat, lng } = await capture();
      setForm(p => ({
        ...p,
        check_in_lat: lat,
        check_in_lng: lng,
        check_in_time: new Date().toISOString(),
        visit_time: new Date().toTimeString().slice(0, 5),
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleSave = async () => {
    if (!form.visit_date) { setError('Visit date is required.'); return; }
    const payload = { ...form };
    if (form.party_type === 'unregistered') {
      if (!form.unregistered_name.trim()) {
        setError('Name / description is required for unregistered visits.');
        return;
      }
      payload.visitor_type = 'unregistered';
      payload.customer_id = null;
      payload.client_id = null;
    } else if (form.party_type === 'client') {
      if (!form.client_id) { setError('Client and visit date are required.'); return; }
      payload.visitor_type = 'client';
      payload.customer_id = null;
      payload.unregistered_name = null;
      payload.unregistered_info = null;
    } else {
      if (!form.customer_id) { setError('Customer and visit date are required.'); return; }
      payload.visitor_type = 'customer';
      payload.client_id = null;
      payload.unregistered_name = null;
      payload.unregistered_info = null;
    }
    delete payload.party_type;
    setSaving(true); setError('');
    try {
      if (modal.mode === 'add') await api.post('/visits', payload);
      else await api.put(`/visits/${modal.item.id}`, payload);
      setModal({ open: false }); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleCheckout = async (visitId) => {
    setCheckoutLoading(visitId);
    try {
      const { lat, lng } = await capture();
      await api.patch(`/visits/${visitId}/checkout`, { check_out_lat: lat, check_out_lng: lng });
      load();
    } catch (e) {
      alert(e.message || 'Checkout failed.');
    } finally { setCheckoutLoading(null); }
  };

  const handleDelete = async () => {
    await api.delete(`/visits/${deleteConfirm.id}`);
    setDeleteConfirm(null); load();
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/visits/${id}/unfreeze`); load(); setModal({ open: false }); }
    catch (err) { setError(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const exportDCR = () => {
    if (!dcrRows.length) return;
    const headers = ['Date','MR Name','Customer','Type','Purpose','Products Discussed','Samples Given','Next Action','Feedback'];
    const csvRows = dcrRows.map(r => [
      r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN') : '',
      r.mr_name || '', r.customer_name || '', r.customer_type || '',
      r.dcr_purpose || '', r.dcr_products_discussed || '',
      r.dcr_samples_given || '', r.dcr_next_action || '', r.dcr_doctor_feedback || '',
    ].map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DCR_Report_${MONTHS[dcrMonth-1]}_${dcrYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const yearOptions = [];
  for (let y = now.getFullYear() - 2; y <= now.getFullYear() + 1; y++) yearOptions.push(y);

  // All visits with geo data for map tab
  const geoVisits = data.data.filter(v => v.check_in_lat != null && v.check_in_lng != null);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Visits</h1>
          <p className="page-subtitle">Track doctor and customer visits by Medical Representatives</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Tab buttons */}
          <button className={`btn ${tab === 'dcr' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(tab === 'dcr' ? 'visits' : 'dcr')}>
            DCR Report
          </button>
          <button className={`btn ${tab === 'map' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(tab === 'map' ? 'visits' : 'map')}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: 4 }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            Map
          </button>
          {tab === 'visits' && (
            <button className={`btn ${calView ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCalView(v => !v)}
              title={calView ? 'Switch to Table View' : 'Switch to Calendar View'}>
              {calView
                ? <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                : <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              }
              {calView ? 'Table' : 'Calendar'}
            </button>
          )}
          {tab === 'visits' && (
            <button className="btn btn-primary" onClick={() => { setCalDayPopup(null); openAdd(); }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Record Visit
            </button>
          )}
        </div>
      </div>

      {/* ── Map Tab ─────────────────────────────────────────────────────────── */}
      {tab === 'map' && <VisitMap visits={data.data} />}

      {/* ── Visits List Tab ──────────────────────────────────────────────────── */}
      {tab === 'visits' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '14px 16px' }}>
              <div className="search-bar">
                <div className="search-input-wrap">
                  <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
                  <input className="form-control" placeholder="Search customer, purpose…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </div>
                <select className="form-control form-select" style={{ width: 160 }} value={outcomeFilter} onChange={e => { setOutcomeFilter(e.target.value); setPage(1); }}>
                  <option value="">All Outcomes</option>
                  {OUTCOMES.map(o => <option key={o} value={o} style={{ textTransform: 'capitalize' }}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── Calendar View ──────────────────────────────────── */}
          {calView && (() => {
            const allVisits = data.data;
            const visitsByDate = {};
            allVisits.forEach(v => {
              const d = v.visit_date ? v.visit_date.split('T')[0] : null;
              if (d) { if (!visitsByDate[d]) visitsByDate[d] = []; visitsByDate[d].push(v); }
            });
            const firstDay = new Date(calYear, calMonth, 1).getDay();
            const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
            const todayStr = localDateStr();
            const prevMonth = () => { if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); } else setCalMonth(m => m - 1); };
            const nextMonth = () => { if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); } else setCalMonth(m => m + 1); };
            const cells = [];
            for (let i = 0; i < firstDay; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(d);
            return (
              <div className="card" style={{ padding: '16px 20px', marginBottom: 16, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <button className="btn btn-secondary btn-sm" onClick={prevMonth}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <span style={{ fontWeight: 700, fontSize: '1rem' }}>{MONTHS_FULL[calMonth]} {calYear}</span>
                  <button className="btn btn-secondary btn-sm" onClick={nextMonth}>
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 14, marginBottom: 12, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  {[['positive','#10b981','Positive'],['neutral','#f59e0b','Neutral'],['negative','#ef4444','Negative']].map(([k,c,l]) => (
                    <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />{l}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2, marginBottom: 4 }}>
                  {WEEKDAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
                  {cells.map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayVisits = visitsByDate[dateStr] || [];
                    const isToday = dateStr === todayStr;
                    const isSelected = calDayPopup?.date === dateStr;
                    return (
                      <div key={day} onClick={() => { if (isSelected) { setCalDayPopup(null); return; } setCalDayPopup({ date: dateStr, visits: dayVisits }); }}
                        style={{ minHeight: 58, padding: '5px 6px', borderRadius: 6, cursor: 'pointer',
                          border: `1.5px solid ${isSelected ? 'var(--primary)' : isToday ? '#bfdbfe' : 'var(--border)'}`,
                          background: isSelected ? 'var(--primary-lighter)' : isToday ? '#f0f9ff' : 'var(--surface)',
                          transition: 'border-color 0.12s, background 0.12s', position: 'relative' }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = 'var(--primary-light, #93c5fd)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = isToday ? '#bfdbfe' : 'var(--border)'; }}
                      >
                        <div style={{ fontSize: '0.78rem', fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--primary)' : 'var(--text-secondary)', marginBottom: 4 }}>{day}</div>
                        {dayVisits.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {dayVisits.slice(0, 6).map((v, i) => (
                              <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: outcomeDotColors[v.outcome] || '#9ca3af', display: 'inline-block' }} />
                            ))}
                            {dayVisits.length > 6 && <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>+{dayVisits.length - 6}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {calDayPopup && (
                  <div style={{ position: 'absolute', top: 60, left: '50%', transform: 'translateX(-50%)',
                    background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.16)', zIndex: 30, minWidth: 280, maxWidth: 400, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                        {new Date(calDayPopup.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-primary btn-sm" onClick={async () => {
                          const dateToFill = calDayPopup.date; setCalDayPopup(null);
                          await openAdd(); setForm(p => ({ ...p, visit_date: dateToFill }));
                        }} style={{ fontSize: '0.72rem', padding: '3px 10px' }}>+ Add Visit</button>
                        <button onClick={() => setCalDayPopup(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, lineHeight: 1 }}>×</button>
                      </div>
                    </div>
                    {calDayPopup.visits.length === 0 ? (
                      <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', padding: '6px 0' }}>No visits on this day.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                        {calDayPopup.visits.map(v => (
                          <div key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', background: 'var(--surface-2)', borderRadius: 7 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: outcomeDotColors[v.outcome], flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{v.visitor_type === 'unregistered' ? v.unregistered_name : (v.client_id ? v.client_name : v.customer_name)}</div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{v.purpose || '—'}{v.user_name ? ` · ${v.user_name}` : ''}</div>
                            </div>
                            <button className="btn btn-ghost btn-icon" style={{ padding: 3 }} onClick={() => { setCalDayPopup(null); setViewModal({ open: true, visit: v }); }}>
                              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Table View ──────────────────────────────────────── */}
          {!calView && <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Customer / Client</th><th>Visit Date</th><th>Purpose</th><th>Outcome</th>
                    <th>Check-in</th><th>DCR</th><th>Next Visit</th><th>MR</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={9}><div className="loading-overlay"><div className="spinner"/></div></td></tr>}
                  {!loading && data.data.length === 0 && <tr><td colSpan={9}><div className="empty-state"><p>No visits recorded yet.</p></div></td></tr>}
                  {!loading && data.data.map(v => (
                    <tr key={v.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: v.is_active === 0 ? 'var(--text-muted)' : undefined }}>
                          {v.visitor_type === 'unregistered' ? v.unregistered_name : (v.client_id ? v.client_name : v.customer_name)}
                          {v.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize: '0.62rem', marginLeft: 6 }}>Frozen</span>}
                          {v.geo_flagged ? (
                            <span title="Check-in location was outside customer geofence" style={{ marginLeft: 6, color: '#ef4444', fontSize: '0.72rem', fontWeight: 700 }}>⚠ Geo</span>
                          ) : null}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                          {v.visitor_type === 'unregistered'
                            ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>Unregistered</span>
                            : v.client_id
                              ? <><span style={{ opacity: 0.7 }}>Client</span>{v.client_type ? ` · ${v.client_type}` : ''}</>
                              : <>{v.customer_type}{v.specialty ? ` · ${v.specialty}` : ''}</>
                          }
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</div>
                        {v.visit_time && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{v.visit_time}</div>}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', maxWidth: 180 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.purpose || '—'}</div>
                      </td>
                      <td><span className={`badge badge-${outcomeColors[v.outcome]}`} style={{ textTransform: 'capitalize' }}>{v.outcome}</span></td>
                      <td style={{ fontSize: '0.78rem' }}>
                        {v.check_in_lat ? (
                          <div>
                            <div style={{ color: '#16a34a', fontWeight: 600 }}>✓ Checked in</div>
                            {v.check_in_time && <div style={{ color: 'var(--text-muted)' }}>{new Date(v.check_in_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</div>}
                            {v.check_out_time ? (
                              <div style={{ color: 'var(--text-muted)' }}>Out: {new Date(v.check_out_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</div>
                            ) : (isPrivileged || v.user_id === user?.id) && v.is_active !== 0 ? (
                              <button className="btn btn-secondary btn-sm" style={{ fontSize: '0.68rem', padding: '2px 7px', marginTop: 3 }}
                                onClick={() => handleCheckout(v.id)} disabled={checkoutLoading === v.id}>
                                {checkoutLoading === v.id ? '…' : 'Check Out'}
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {v.dcr_submitted
                          ? <span style={{ color: '#16a34a', fontSize: '1rem' }}>&#10003;</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>
                        }
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                        {v.next_visit_date ? new Date(v.next_visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{v.user_name}</td>
                      <td>
                        <div className="table-actions">
                          <button className="btn btn-ghost btn-icon" data-tooltip="View" onClick={() => setViewModal({ open: true, visit: v })}>
                            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          {(isPrivileged || v.user_id === user?.id) && (
                            <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(v)}>
                              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </button>
                          )}
                          {(isPrivileged || v.user_id === user?.id) && v.is_active !== 0 && (
                            <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(v)}>
                              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
          </div>}
        </>
      )}

      {/* ── DCR Report Tab ───────────────────────────────────────────────────── */}
      {tab === 'dcr' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>Month:</label>
                  <select className="form-control form-select" style={{ width: 110 }} value={dcrMonth} onChange={e => setDcrMonth(parseInt(e.target.value))}>
                    {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>Year:</label>
                  <select className="form-control form-select" style={{ width: 95 }} value={dcrYear} onChange={e => setDcrYear(parseInt(e.target.value))}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {isPrivileged && dcrUsers.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 500 }}>MR:</label>
                    <select className="form-control form-select" style={{ width: 160 }} value={dcrUserId} onChange={e => setDcrUserId(e.target.value)}>
                      <option value="">All MRs</option>
                      {dcrUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                )}
                <button className="btn btn-secondary" onClick={loadDCR}>Refresh</button>
                <button className="btn btn-secondary" onClick={exportDCR} disabled={!dcrRows.length}>Export CSV</button>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>MR Name</th><th>Customer</th><th>Purpose</th>
                    <th>Products Discussed</th><th>Samples Given</th><th>Next Action</th><th>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {dcrLoading && <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>}
                  {!dcrLoading && dcrRows.length === 0 && (
                    <tr><td colSpan={8}><div className="empty-state"><p>No submitted DCRs for this period.</p></div></td></tr>
                  )}
                  {!dcrLoading && dcrRows.map(r => (
                    <tr key={r.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.83rem' }}>
                        {r.visit_date ? new Date(r.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                      </td>
                      <td style={{ fontWeight: 500 }}>{r.mr_name || '—'}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.customer_name || '—'}</div>
                        {r.customer_type && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{r.customer_type}</div>}
                      </td>
                      <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.dcr_purpose || '—'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_products_discussed || '—'}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_samples_given || '—'}</td>
                      <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_next_action || '—'}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{r.dcr_doctor_feedback || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── Add/Edit Modal ───────────────────────────────────────────────────── */}
      <Modal open={modal.open} onClose={() => setModal({ open: false })} title={modal.mode === 'add' ? 'Record Visit' : 'Edit Visit'}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
          {modal.item?.is_active === 0
            ? <button className="btn btn-primary" style={{ background: '#dc2626' }} onClick={() => handleUnfreeze(modal.item.id)}>Unfreeze</button>
            : <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Visit'}</button>
          }
        </>}
      >
        {error && <div className="alert alert-error">{error}</div>}
        {modal.item?.is_active === 0 && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>Record Frozen</div>
            <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This record is frozen and cannot be edited. Unfreeze to make changes.</div>
          </div>
        )}

        {/* ── GPS Check-In Section ──────────────────────────────────────── */}
        {modal.item?.is_active !== 0 && (
          <div style={{ marginBottom: 16, padding: '12px 14px', background: form.check_in_lat ? '#f0fdf4' : '#f8fafc', border: `1px solid ${form.check_in_lat ? '#86efac' : 'var(--border)'}`, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: form.check_in_lat ? '#15803d' : 'var(--text-secondary)' }}>
                  {form.check_in_lat ? '✓ Location Captured' : 'GPS Check-In'}
                </div>
                {form.check_in_lat ? (
                  <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: 2 }}>
                    {Number(form.check_in_lat).toFixed(5)}, {Number(form.check_in_lng).toFixed(5)}
                    {form.check_in_time && <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>{new Date(form.check_in_time).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}</span>}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Capture GPS when you arrive at the customer location</div>
                )}
              </div>
              <button
                type="button"
                className={`btn ${form.check_in_lat ? 'btn-secondary' : 'btn-primary'}`}
                style={{ fontSize: '0.82rem', padding: '6px 14px' }}
                onClick={handleCheckIn}
                disabled={gpsState === 'loading'}
              >
                {gpsState === 'loading' ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="spinner" style={{ width: 12, height: 12 }} />Locating…
                  </span>
                ) : form.check_in_lat ? 'Re-capture Location' : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    Check In with GPS
                  </span>
                )}
              </button>
            </div>
            {gpsError && <div style={{ marginTop: 8, fontSize: '0.78rem', color: '#dc2626' }}>{gpsError}</div>}
          </div>
        )}

        {/* Party type selector */}
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Party Type *</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['customer','client','unregistered'].map(pt => (
              <label key={pt} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: modal.item?.is_active === 0 ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: form.party_type === pt ? 600 : 400 }}>
                <input type="radio" name="party_type" value={pt} checked={form.party_type === pt}
                  disabled={modal.item?.is_active === 0}
                  onChange={() => setForm(p => ({ ...p, party_type: pt, customer_id: '', client_id: '', unregistered_name: '', unregistered_info: '' }))} />
                {pt === 'unregistered' ? 'Unregistered' : pt.charAt(0).toUpperCase() + pt.slice(1)}
              </label>
            ))}
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            {form.party_type === 'unregistered' ? (
              <>
                <label className="form-label">Name / Description *</label>
                <input className="form-control" value={form.unregistered_name} onChange={e => f('unregistered_name', e.target.value)} placeholder="Enter person or business name" disabled={modal.item?.is_active === 0} />
              </>
            ) : form.party_type === 'client' ? (
              <>
                <label className="form-label">Client *</label>
                <select className="form-control form-select" value={form.client_id} onChange={e => f('client_id', e.target.value)} disabled={modal.item?.is_active === 0}>
                  <option value="">Select client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.type ? ` (${c.type})` : ''}</option>)}
                </select>
              </>
            ) : (
              <>
                <label className="form-label">Customer *</label>
                <select className="form-control form-select" value={form.customer_id} onChange={e => f('customer_id', e.target.value)} disabled={modal.item?.is_active === 0}>
                  <option value="">Select customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                </select>
              </>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Outcome</label>
            <select className="form-control form-select" value={form.outcome} onChange={e => f('outcome', e.target.value)} disabled={modal.item?.is_active === 0}>
              {OUTCOMES.map(o => <option key={o} value={o} style={{ textTransform: 'capitalize' }}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
            </select>
          </div>
        </div>
        {form.party_type === 'unregistered' && (
          <div className="form-group">
            <label className="form-label">Additional Info</label>
            <input className="form-control" value={form.unregistered_info} onChange={e => f('unregistered_info', e.target.value)} placeholder="Phone, address, company, or other details…" disabled={modal.item?.is_active === 0} />
          </div>
        )}
        <div className="form-row">
          <div className="form-group"><label className="form-label">Visit Date *</label><input className="form-control" type="date" value={form.visit_date} onChange={e => f('visit_date', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Visit Time</label><input className="form-control" type="time" value={form.visit_time} onChange={e => f('visit_time', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-group"><label className="form-label">Purpose</label><input className="form-control" value={form.purpose} onChange={e => f('purpose', e.target.value)} placeholder="Product promotion, Follow-up, Sample distribution…" disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Products Discussed</label><input className="form-control" value={form.products_discussed} onChange={e => f('products_discussed', e.target.value)} placeholder="Paracetamol 500mg, Amoxicillin 250mg…" disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Samples Given</label><input className="form-control" value={form.samples_given} onChange={e => f('samples_given', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={3} value={form.notes} onChange={e => f('notes', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        <div className="form-group"><label className="form-label">Next Visit Date</label><input className="form-control" type="date" value={form.next_visit_date} onChange={e => f('next_visit_date', e.target.value)} disabled={modal.item?.is_active === 0} /></div>

        {/* DCR Section */}
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <button type="button" onClick={() => setDcrOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', color: 'var(--primary)', padding: 0, marginBottom: dcrOpen ? 12 : 0 }}
            disabled={modal.item?.is_active === 0}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ transform: dcrOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
            DCR Details
          </button>
          {dcrOpen && (
            <div>
              <div className="form-group"><label className="form-label">Purpose of Visit (DCR)</label><input className="form-control" value={form.dcr_purpose} onChange={e => f('dcr_purpose', e.target.value)} placeholder="Detailed purpose for DCR…" disabled={modal.item?.is_active === 0} /></div>
              <div className="form-group"><label className="form-label">Products Discussed (DCR)</label><textarea className="form-control" rows={2} value={form.dcr_products_discussed} onChange={e => f('dcr_products_discussed', e.target.value)} placeholder="List products discussed…" disabled={modal.item?.is_active === 0} /></div>
              <div className="form-group"><label className="form-label">Samples Given (DCR)</label><textarea className="form-control" rows={2} value={form.dcr_samples_given} onChange={e => f('dcr_samples_given', e.target.value)} placeholder="Samples distributed details…" disabled={modal.item?.is_active === 0} /></div>
              <div className="form-group"><label className="form-label">Doctor Feedback</label><textarea className="form-control" rows={2} value={form.dcr_doctor_feedback} onChange={e => f('dcr_doctor_feedback', e.target.value)} placeholder="Feedback from doctor or chemist…" disabled={modal.item?.is_active === 0} /></div>
              <div className="form-group"><label className="form-label">Next Action</label><input className="form-control" value={form.dcr_next_action} onChange={e => f('dcr_next_action', e.target.value)} placeholder="Follow-up action required…" disabled={modal.item?.is_active === 0} /></div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: modal.item?.is_active === 0 ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                  <input type="checkbox" checked={!!form.dcr_submitted} onChange={e => f('dcr_submitted', e.target.checked)} disabled={modal.item?.is_active === 0} />
                  Submit DCR (mark as officially submitted)
                </label>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* ── View Modal ───────────────────────────────────────────────────────── */}
      <Modal open={viewModal.open} onClose={() => setViewModal({ open: false })} title="Visit Details"
        footer={<button className="btn btn-secondary" onClick={() => setViewModal({ open: false })}>Close</button>}
      >
        {viewModal.visit && (() => {
          const v = viewModal.visit;
          return (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{v.visitor_type === 'unregistered' ? v.unregistered_name : (v.client_id ? v.client_name : v.customer_name)}</div>
                  <div style={{ fontSize: '0.83rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                    {v.visitor_type === 'unregistered'
                      ? <span style={{ color: '#f59e0b', fontWeight: 600 }}>Unregistered{v.unregistered_info ? ` · ${v.unregistered_info}` : ''}</span>
                      : v.client_id
                        ? <>Client{v.client_type ? ` · ${v.client_type}` : ''}</>
                        : <>{v.customer_type}{v.specialty ? ` · ${v.specialty}` : ''}</>
                    }
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <span className={`badge badge-${outcomeColors[v.outcome]}`} style={{ textTransform: 'capitalize' }}>{v.outcome}</span>
                  {v.dcr_submitted ? <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>DCR Submitted</span> : null}
                  {v.geo_flagged ? <span className="badge badge-error" style={{ fontSize: '0.65rem' }}>⚠ Geo Flagged</span> : null}
                </div>
              </div>
              <div className="detail-grid" style={{ marginBottom: 14 }}>
                <div className="detail-item"><span className="detail-label">Visit Date</span><span className="detail-value">{new Date(v.visit_date).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</span></div>
                <div className="detail-item"><span className="detail-label">MR</span><span className="detail-value">{v.user_name}</span></div>
                <div className="detail-item"><span className="detail-label">Purpose</span><span className="detail-value">{v.purpose || '—'}</span></div>
                <div className="detail-item"><span className="detail-label">Next Visit</span><span className="detail-value">{v.next_visit_date ? new Date(v.next_visit_date).toLocaleDateString('en-IN') : '—'}</span></div>
              </div>

              {/* Geo check-in/out info */}
              {v.check_in_lat && (
                <div style={{ marginBottom: 12, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#15803d', marginBottom: 6 }}>
                    GPS Check-in Data
                    {v.geo_flagged ? <span style={{ marginLeft: 8, color: '#ef4444' }}>⚠ Outside 500m geofence</span> : <span style={{ marginLeft: 8, color: '#15803d' }}>✓ Within geofence</span>}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                    <div>Check-in: <strong>{v.check_in_time ? new Date(v.check_in_time).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}</strong></div>
                    <div>Location: <strong>{Number(v.check_in_lat).toFixed(5)}, {Number(v.check_in_lng).toFixed(5)}</strong></div>
                    {v.check_out_time && <div>Check-out: <strong>{new Date(v.check_out_time).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</strong></div>}
                    {v.check_in_address && <div>Address: <strong>{v.check_in_address}</strong></div>}
                  </div>
                </div>
              )}

              {v.products_discussed && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Products Discussed</div><div style={{ fontSize: '0.875rem', background: 'var(--primary-lighter)', padding: '8px 12px', borderRadius: 8, color: 'var(--primary-dark)' }}>{v.products_discussed}</div></div>}
              {v.samples_given && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Samples Given</div><div style={{ fontSize: '0.875rem' }}>{v.samples_given}</div></div>}
              {v.notes && <div style={{ marginBottom: 10 }}><div className="detail-label" style={{ marginBottom: 4 }}>Notes</div><div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '10px 12px', borderRadius: 8, color: 'var(--text-secondary)' }}>{v.notes}</div></div>}
              {(v.dcr_purpose || v.dcr_products_discussed || v.dcr_doctor_feedback) && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--primary)', marginBottom: 8 }}>DCR Details</div>
                  {v.dcr_purpose && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>DCR Purpose</div><div style={{ fontSize: '0.875rem' }}>{v.dcr_purpose}</div></div>}
                  {v.dcr_products_discussed && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Products Discussed</div><div style={{ fontSize: '0.875rem' }}>{v.dcr_products_discussed}</div></div>}
                  {v.dcr_samples_given && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Samples Given</div><div style={{ fontSize: '0.875rem' }}>{v.dcr_samples_given}</div></div>}
                  {v.dcr_doctor_feedback && <div style={{ marginBottom: 8 }}><div className="detail-label" style={{ marginBottom: 2 }}>Doctor Feedback</div><div style={{ fontSize: '0.875rem', background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 8 }}>{v.dcr_doctor_feedback}</div></div>}
                  {v.dcr_next_action && <div><div className="detail-label" style={{ marginBottom: 2 }}>Next Action</div><div style={{ fontSize: '0.875rem' }}>{v.dcr_next_action}</div></div>}
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {/* ── Freeze Confirm ───────────────────────────────────────────────────── */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Visit"
        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}
      >
        <p>Are you sure you want to freeze this visit record for <strong>{deleteConfirm?.visitor_type === 'unregistered' ? deleteConfirm?.unregistered_name : (deleteConfirm?.client_id ? deleteConfirm?.client_name : deleteConfirm?.customer_name)}</strong>? The record will be locked. You can unfreeze it later.</p>
      </Modal>
    </div>
  );
}
